import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiImage, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useLanguage } from '../../context/LanguageContext';

const Container = styled.div`
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #4dd1a0;
    transform: translateY(-2px);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #4dd1a0;
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;

  &:hover {
    border-color: #5efba9;
    background: #f0fdf4;
  }

  input {
    display: none;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const TableContainer = styled.div`
  ${scrollableTableWrapStyles}
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
`;

const TableRow = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s;
  color: #6b7280;
  margin-right: 8px;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

export default function SuperAdminSubcategories() {
  const { isArabic } = useLanguage();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    category_id: '',
    name_en: '',
    name_ar: '',
    slug: '',
    img: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.createRef();

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name_en', { ascending: true });

      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subcategories')
        .select(`
          *,
          category:categories(name_en, name_ar)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSubcategories(data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      alert(`Error fetching subcategories: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleAutoSlug = () => {
    if (formData.name_en && !formData.slug) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(prev.name_en)
      }));
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `subcategory_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      console.log('Uploading file to subcategories bucket:', fileName);

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('subcategories')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        alert('Failed to upload image: ' + uploadError.message);
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('subcategories')
        .getPublicUrl(fileName);

      console.log('Upload successful, public URL:', publicUrl);

      // Automatically set the img field with the uploaded URL
      setFormData(prev => ({
        ...prev,
        img: publicUrl
      }));

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingSubcategory) {
        // Update existing subcategory
        const { error } = await supabase
          .from('subcategories')
          .update({
            category_id: formData.category_id,
            name_en: formData.name_en,
            name_ar: formData.name_ar,
            slug: formData.slug || generateSlug(formData.name_en),
            img: formData.img || null
          })
          .eq('id', editingSubcategory.id);

        if (error) throw error;
        alert('Subcategory updated successfully!');
      } else {
        // Add new subcategory
        const { error } = await supabase
          .from('subcategories')
          .insert({
            category_id: formData.category_id,
            name_en: formData.name_en,
            name_ar: formData.name_ar,
            slug: formData.slug || generateSlug(formData.name_en),
            img: formData.img || null
          });

        if (error) throw error;
        alert('Subcategory added successfully!');
      }

      // Reset form
      setFormData({
        category_id: '',
        name_en: '',
        name_ar: '',
        slug: '',
        img: ''
      });
      setIsModalOpen(false);
      setEditingSubcategory(null);
      fetchSubcategories();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      alert(`Error saving subcategory: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      category_id: subcategory.category_id || '',
      name_en: subcategory.name_en || '',
      name_ar: subcategory.name_ar || '',
      slug: subcategory.slug || '',
      img: subcategory.img || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('subcategories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      alert('Subcategory deleted successfully!');
      fetchSubcategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert(`Error deleting subcategory: ${error.message}`);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSubcategory(null);
    setFormData({
      category_id: '',
      name_en: '',
      name_ar: '',
      slug: '',
      img: ''
    });
  };

  // Filter subcategories by search term
  const filteredSubcategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return subcategories;
    }
    const term = searchTerm.toLowerCase();
    return subcategories.filter(subcategory => {
      const nameEn = (subcategory.name_en || '').toLowerCase();
      const nameAr = (subcategory.name_ar || '').toLowerCase();
      const slug = (subcategory.slug || '').toLowerCase();
      const categoryName = (subcategory.category?.name_en || '').toLowerCase();
      const categoryNameAr = (subcategory.category?.name_ar || '').toLowerCase();
      return nameEn.includes(term) || nameAr.includes(term) || slug.includes(term) || categoryName.includes(term) || categoryNameAr.includes(term);
    });
  }, [subcategories, searchTerm]);

  return (
    <>
      <Container>
        <Header>
          <Title>{isArabic ? 'إدارة الفئات الفرعية' : 'Subcategories Management'}</Title>
          <AddButton onClick={() => setIsModalOpen(true)}>
            <FiPlus /> {isArabic ? 'إضافة فئة فرعية' : 'Add Subcategory'}
          </AddButton>
        </Header>

        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder={isArabic ? 'ابحث بالاسم أو المعرف أو الفئة...' : 'Search by name, slug, or category...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')} title={isArabic ? 'مسح البحث' : 'Clear search'}>
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>

        <Card>
          <TableContainer>
            <Table>
              <thead>
                <TableRow>
                  <TableHeaderCell>{isArabic ? 'الصورة' : 'Image'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الفئة الرئيسية' : 'Main Category'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الفئة الفرعية (EN)' : 'Subcategory (EN)'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الفئة الفرعية (AR)' : 'Subcategory (AR)'}</TableHeaderCell>
                  <TableHeaderCell>Slug</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
                </TableRow>
              </thead>
              <tbody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                      {isArabic ? 'جارٍ التحميل...' : 'Loading...'}
                    </TableCell>
                  </TableRow>
                ) : subcategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState>
                        <p>{isArabic ? 'لا توجد فئات فرعية' : 'No subcategories found'}</p>
                        <p>{isArabic ? 'اضغط "إضافة فئة فرعية" لإضافة أول فئة فرعية' : 'Click "Add Subcategory" to add your first subcategory'}</p>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                ) : filteredSubcategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState>
                        <p>{searchTerm ? (isArabic ? 'لا توجد نتائج مطابقة للبحث' : 'No subcategories found matching your search') : (isArabic ? 'لا توجد فئات فرعية متاحة' : 'No subcategories available')}</p>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubcategories.map(subcategory => (
                    <TableRow key={subcategory.id}>
                      <TableCell>
                        {subcategory.img ? (
                          <img 
                            src={subcategory.img} 
                            alt={subcategory.name_en || 'Subcategory'} 
                            style={{ 
                              width: '60px', 
                              height: '60px', 
                              objectFit: 'cover', 
                              borderRadius: '8px' 
                            }}
                          />
                        ) : (
                          <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            background: '#f3f4f6', 
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#9ca3af',
                            fontSize: '12px'
                          }}>
                            {isArabic ? 'لا صورة' : 'No Image'}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>
                          {subcategory.category?.name_en || (isArabic ? 'غير متوفر' : 'N/A')}
                        </div>
                        {subcategory.category?.name_ar && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                            {subcategory.category.name_ar}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <strong>{subcategory.name_en || (isArabic ? 'غير متوفر' : 'N/A')}</strong>
                      </TableCell>
                      <TableCell>{subcategory.name_ar || (isArabic ? 'غير متوفر' : 'N/A')}</TableCell>
                      <TableCell>
                        <code style={{ 
                          background: '#f3f4f6', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {subcategory.slug || (isArabic ? 'غير متوفر' : 'N/A')}
                        </code>
                      </TableCell>
                      <TableCell>
                        {subcategory.created_at
                          ? new Date(subcategory.created_at).toLocaleDateString()
                          : (isArabic ? 'غير متوفر' : 'N/A')}
                      </TableCell>
                      <TableCell>
                        <ActionButton onClick={() => handleEdit(subcategory)} title={isArabic ? 'تعديل' : 'Edit'}>
                          <FiEdit2 />
                        </ActionButton>
                        <ActionButton onClick={() => handleDelete(subcategory.id)} title={isArabic ? 'حذف' : 'Delete'}>
                          <FiTrash2 />
                        </ActionButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingSubcategory ? (isArabic ? 'تعديل الفئة الفرعية' : 'Edit Subcategory') : (isArabic ? 'إضافة فئة فرعية جديدة' : 'Add New Subcategory')}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup className="full-width">
                  <Label>{isArabic ? 'الفئة الرئيسية *' : 'Main Category *'}</Label>
                  <Select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{isArabic ? 'اختر فئة رئيسية' : 'Select a main category'}</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name_en} {category.name_ar && `(${category.name_ar})`}
                      </option>
                    ))}
                  </Select>
                  {editingSubcategory && formData.category_id && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px', 
                      background: '#f0fdf4', 
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#166534'
                    }}>
                      {isArabic ? 'الحالي:' : 'Current:'} {categories.find(c => c.id === formData.category_id)?.name_en || (isArabic ? 'غير متوفر' : 'N/A')}
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>{isArabic ? 'الاسم الإنجليزي *' : 'English Name *'}</Label>
                  <Input
                    type="text"
                    name="name_en"
                    value={formData.name_en}
                    onChange={handleInputChange}
                    onBlur={handleAutoSlug}
                    required
                    placeholder={isArabic ? 'مثال: T-Shirts' : 'e.g., T-Shirts'}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{isArabic ? 'الاسم العربي *' : 'Arabic Name *'}</Label>
                  <Input
                    type="text"
                    name="name_ar"
                    value={formData.name_ar}
                    onChange={handleInputChange}
                    required
                    placeholder="مثال: تي شيرت"
                  />
                </FormGroup>

                <FormGroup className="full-width">
                  <Label>{isArabic ? 'المعرف (Slug) *' : 'Slug *'}</Label>
                  <Input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    placeholder={isArabic ? 'يُولد تلقائيًا من الاسم الإنجليزي' : 'auto-generated from English name'}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                    {isArabic ? 'معرف مناسب للرابط (يُولد تلقائيًا إذا تُرك فارغًا)' : 'URL-friendly identifier (auto-generated from English name if left empty)'}
                  </small>
                </FormGroup>

                <FormGroup className="full-width">
                  <Label>{isArabic ? 'صورة الفئة الفرعية' : 'Subcategory Image'}</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    disabled={uploadingImage}
                    style={{ display: 'none' }}
                  />
                  <UploadArea onClick={() => fileInputRef.current?.click()}>
                    {uploadingImage ? (
                      <div>
                        <p style={{ color: '#5efba9', margin: 0 }}>{isArabic ? 'جارٍ الرفع...' : 'Uploading...'}</p>
                      </div>
                    ) : (
                      <>
                        <FiImage style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '12px' }} />
                        <p style={{ color: '#6b7280', margin: 0 }}>
                          {isArabic ? 'اضغط لاختيار صورة' : 'Click to select an image file'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                          {isArabic ? 'الامتدادات: JPG, PNG, GIF, WebP' : 'Supports: JPG, PNG, GIF, WebP'}
                        </p>
                      </>
                    )}
                  </UploadArea>
                  {formData.img && (
                    <ImagePreview src={formData.img} alt={isArabic ? 'معاينة الفئة الفرعية' : 'Subcategory preview'} />
                  )}
                  {formData.img && (
                    <Input
                      type="text"
                      name="img"
                      value={formData.img}
                      onChange={handleInputChange}
                      placeholder={isArabic ? 'رابط الصورة (يُملأ تلقائيًا بعد الرفع)' : 'Image URL (auto-filled after upload)'}
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </FormGroup>
              </FormGrid>

              <ButtonGroup>
                <CancelButton type="button" onClick={handleCloseModal}>
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </CancelButton>
                <SaveButton type="submit" disabled={submitting}>
                  {submitting
                    ? (isArabic ? 'جارٍ الحفظ...' : 'Saving...')
                    : editingSubcategory
                      ? (isArabic ? 'تحديث الفئة الفرعية' : 'Update Subcategory')
                      : (isArabic ? 'إضافة فئة فرعية' : 'Add Subcategory')}
                </SaveButton>
              </ButtonGroup>
            </form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  );
}

